/**
 * Framework-free WebGL aura renderer.
 * Compiles the aura GLSL shader, runs a requestAnimationFrame loop,
 * and drives uniforms from AgentDisplayState + mic volume.
 */
import { shaderSource } from "./shader-source.js";
import { processUniform } from "./uniforms.js";
// Pulse period = 2 * Framer duration (mirror = one full sine cycle per 2*D seconds).
// Original DEFAULT_PULSE_TRANSITION had duration: 0.8, so period = 1.6s.
const PULSE_PERIOD = 1.6;
const ENVELOPES = {
    idle: { speed: 10, scale: 0.2, amplitude: 1.2, frequency: 0.4, brightness: 1.0 },
    // listening pulsed [1.5, 2.0] with DEFAULT_PULSE_TRANSITION (use-aura-visualizer.ts:99)
    listening: {
        speed: 20,
        scale: 0.3,
        amplitude: 1.0,
        frequency: 0.7,
        brightness: 1.75,
        brightnessPulse: { lo: 1.5, hi: 2.0, period: PULSE_PERIOD },
    },
    // thinking/initializing pulsed [0.5, 2.5] (use-aura-visualizer.ts:108)
    thinking: {
        speed: 30,
        scale: 0.3,
        amplitude: 0.5,
        frequency: 1.0,
        brightness: 1.5,
        brightnessPulse: { lo: 0.5, hi: 2.5, period: PULSE_PERIOD },
    },
    initializing: {
        speed: 30,
        scale: 0.3,
        amplitude: 0.5,
        frequency: 1.0,
        brightness: 1.5,
        brightnessPulse: { lo: 0.5, hi: 2.5, period: PULSE_PERIOD },
    },
    speaking: { speed: 70, scale: 0.3, amplitude: 0.75, frequency: 1.25, brightness: 1.5 },
};
// --------------- Shader boilerplate (adapted from react-shader-toy.tsx) ---------------
const BASIC_VS = `attribute vec3 aVertexPosition;
void main(void) {
    gl_Position = vec4(aVertexPosition, 1.0);
}`;
const FS_MAIN_SHADER = `\nvoid main(void){
    vec4 color = vec4(0.0,0.0,0.0,1.0);
    mainImage( color, gl_FragCoord.xy );
    gl_FragColor = color;
}`;
function buildFragmentShader(source, dprValue) {
    const precision = `precision highp float;\n`;
    const dpr = `#define DPR ${dprValue.toFixed(6)}\n`;
    const uniformDecls = [
        "uniform float uSpeed;",
        "uniform float uBlur;",
        "uniform float uScale;",
        "uniform float uShape;",
        "uniform float uFrequency;",
        "uniform float uAmplitude;",
        "uniform float uBloom;",
        "uniform float uMix;",
        "uniform float uSpacing;",
        "uniform float uColorShift;",
        "uniform float uVariance;",
        "uniform float uSmoothing;",
        "uniform float uMode;",
        "uniform vec3 uColor;",
        "uniform vec2 iResolution;",
        "uniform float iTime;",
    ]
        .map((d) => d + " \n")
        .join("");
    return precision + dpr + uniformDecls + source + FS_MAIN_SHADER;
}
// --------------- Color helpers ---------------
function hexToRgb(hex) {
    const h = hex.replace(/^#/, "");
    const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
    const n = parseInt(full, 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}
function lerpVal(from, to, t) {
    return from + (to - from) * t;
}
// --------------- Factory ---------------
export function createAuraRenderer(canvas, opts) {
    const glMaybe = canvas.getContext("webgl");
    if (!glMaybe) {
        // No WebGL; return a no-op renderer. Widget falls back to CSS animation.
        return {
            setState() { },
            setVolume() { },
            destroy() { },
        };
    }
    // Narrowed to non-null; aliased so inner closures keep the narrowed type.
    const gl = glMaybe;
    // DPR capped at 1.5 to match original; guard for jsdom where devicePixelRatio may be undefined.
    const dpr = Math.min(typeof devicePixelRatio !== "undefined" ? devicePixelRatio : 1, 1.5);
    // ---- Build + compile shaders ----
    const fsSource = buildFragmentShader(shaderSource, dpr);
    function compileShader(type, source) {
        const shader = gl.createShader(type);
        if (!shader)
            return null;
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            gl.deleteShader(shader);
            return null;
        }
        return shader;
    }
    const vs = compileShader(gl.VERTEX_SHADER, BASIC_VS);
    const fs = compileShader(gl.FRAGMENT_SHADER, fsSource);
    const program = gl.createProgram();
    if (!program || !vs || !fs) {
        if (vs)
            gl.deleteShader(vs);
        if (fs)
            gl.deleteShader(fs);
        if (program)
            gl.deleteProgram(program);
        return { setState() { }, setVolume() { }, destroy() { } };
    }
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        gl.deleteShader(vs);
        gl.deleteShader(fs);
        gl.deleteProgram(program);
        return { setState() { }, setVolume() { }, destroy() { } };
    }
    gl.useProgram(program);
    // Flag shaders for deletion now; GL frees them with the program in destroy().
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    // ---- Quad buffer ----
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    const vertices = new Float32Array([1, 1, 0, -1, 1, 0, 1, -1, 0, -1, -1, 0]);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
    const posAttr = gl.getAttribLocation(program, "aVertexPosition");
    gl.enableVertexAttribArray(posAttr);
    // ---- Uniform locations (lazily cached) ----
    const locCache = new Map();
    function loc(name) {
        if (!locCache.has(name))
            locCache.set(name, gl.getUniformLocation(program, name));
        return locCache.get(name) ?? null;
    }
    function setFloat(name, v) {
        const l = loc(name);
        if (l)
            processUniform(gl, l, "1f", v);
    }
    function setVec3(name, v) {
        const l = loc(name);
        if (l)
            processUniform(gl, l, "3f", v);
    }
    function setVec2(name, x, y) {
        const l = loc(name);
        if (l)
            processUniform(gl, l, "2f", [x, y]);
    }
    // ---- Mutable render state ----
    let currentState = "idle";
    let currentVolume = 0;
    // Current interpolated envelope values.
    let speed = ENVELOPES.idle.speed;
    let scale = ENVELOPES.idle.scale;
    let amplitude = ENVELOPES.idle.amplitude;
    let frequency = ENVELOPES.idle.frequency;
    let brightness = ENVELOPES.idle.brightness;
    const colorRgb = hexToRgb(opts.color);
    const mode = opts.themeMode === "light" ? 1.0 : 0.0;
    const colorShift = opts.colorShift ?? 0.05;
    let timer = 0;
    let lastTimestamp = 0;
    let rafId = 0;
    // ---- Render loop ----
    function frame(ts) {
        const delta = lastTimestamp ? (ts - lastTimestamp) / 1000 : 0;
        lastTimestamp = ts;
        timer += delta;
        // Lerp envelope toward target (60fps: t=0.05 ~= 0.5s half-life)
        const target = ENVELOPES[currentState];
        const t = Math.min(1, delta * 6);
        speed = lerpVal(speed, target.speed, t);
        amplitude = lerpVal(amplitude, target.amplitude, t);
        frequency = lerpVal(frequency, target.frequency, t);
        // Brightness: pulsing states use a time-based sine mirror pulse; static states lerp to target.
        if (target.brightnessPulse) {
            const { lo, hi, period } = target.brightnessPulse;
            brightness = lo + (hi - lo) * 0.5 * (1 + Math.sin((2 * Math.PI * timer) / period));
        }
        else {
            brightness = lerpVal(brightness, target.brightness, t);
        }
        // While speaking, drive scale from mic volume.
        const targetScale = currentState === "speaking"
            ? 0.2 + 0.2 * currentVolume
            : target.scale;
        scale = lerpVal(scale, targetScale, t);
        // Resize canvas backing store to CSS pixels * DPR. Skip when the canvas has
        // no layout size (hidden container, jsdom); never derive the CSS size from
        // the backing store, which would compound by DPR every frame.
        const cssW = canvas.clientWidth;
        const cssH = canvas.clientHeight;
        if (cssW > 0 && cssH > 0) {
            const backW = Math.round(cssW * dpr);
            const backH = Math.round(cssH * dpr);
            if (canvas.width !== backW || canvas.height !== backH) {
                canvas.width = backW;
                canvas.height = backH;
            }
        }
        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.vertexAttribPointer(posAttr, 3, gl.FLOAT, false, 0, 0);
        // Write uniforms.
        setFloat("uSpeed", speed);
        setFloat("uBlur", 0.2);
        setFloat("uScale", scale);
        setFloat("uShape", 1.0);
        setFloat("uFrequency", frequency);
        setFloat("uAmplitude", amplitude);
        setFloat("uBloom", 0.0);
        setFloat("uMix", brightness);
        setFloat("uSpacing", 0.5);
        setFloat("uColorShift", colorShift);
        setFloat("uVariance", 0.1);
        setFloat("uSmoothing", 1.0);
        setFloat("uMode", mode);
        setVec3("uColor", colorRgb);
        setFloat("iTime", timer);
        setVec2("iResolution", canvas.width, canvas.height);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        rafId = requestAnimationFrame(frame);
    }
    rafId = requestAnimationFrame(frame);
    // ---- Public API ----
    return {
        setState(s) {
            currentState = s;
        },
        setVolume(v) {
            currentVolume = v;
        },
        destroy() {
            cancelAnimationFrame(rafId);
            // No loseContext(): AuraShader reuses the same canvas when color/theme/
            // colorShift change, and a lost context breaks the next renderer.
            gl.deleteProgram(program);
            gl.deleteBuffer(buffer);
        },
    };
}
